import { RARE_LETTER_SET } from '../../config/boardDistributions/letterWeights'
import type { LetterWeight } from './fill'
import { nextInteger } from './rng'

export const LETTER_BAG_BUCKET_COUNT = 4

export type BoggleLetterBags = {
  consonants: string[]
  vowels: string[]
}

export type ClassifiedLetterWeights = {
  consonants: ReadonlyArray<LetterWeight>
  vowels: ReadonlyArray<LetterWeight>
}

type BuildBucketBalancedDeckOptions = {
  rareLetterSet?: ReadonlySet<string>
}

function shuffleInPlace<T>(items: T[], rngState: number): number {
  let current = rngState

  for (let index = items.length - 1; index > 0; index -= 1) {
    const rolled = nextInteger(current, index + 1)
    current = rolled.rngState
    const swapIndex = rolled.value
    ;[items[index], items[swapIndex]] = [items[swapIndex]!, items[index]!]
  }

  return current
}

function pickDistinctBucketIndices(
  count: number,
  bucketCount: number,
  rngState: number,
): { bucketIndices: number[]; rngState: number } {
  const available = Array.from({ length: bucketCount }, (_, index) => index)
  const bucketIndices: number[] = []
  let current = rngState

  for (let pick = 0; pick < count; pick += 1) {
    const rolled = nextInteger(current, available.length)
    current = rolled.rngState
    const chosenIndex = available.splice(rolled.value, 1)[0]!
    bucketIndices.push(chosenIndex)
  }

  return { bucketIndices, rngState: current }
}

export function distributePoolAcrossBucketsEvenly(
  pool: string[],
  bucketCount: number,
  rngState: number,
): { buckets: string[][]; rngState: number } {
  const buckets = Array.from({ length: bucketCount }, () => [] as string[])
  const groupCounts = Array.from({ length: bucketCount }, () => 0)
  let current = rngState

  for (const letter of pool) {
    let minCount = groupCounts[0]!
    for (let index = 1; index < bucketCount; index += 1) {
      minCount = Math.min(minCount, groupCounts[index]!)
    }

    const candidates: number[] = []
    for (let index = 0; index < bucketCount; index += 1) {
      if (groupCounts[index] === minCount) {
        candidates.push(index)
      }
    }

    const rolled = nextInteger(current, candidates.length)
    current = rolled.rngState
    const bucketIndex = candidates[rolled.value]!
    buckets[bucketIndex]!.push(letter)
    groupCounts[bucketIndex] = (groupCounts[bucketIndex] ?? 0) + 1
  }

  return { buckets, rngState: current }
}

function letterWeightToCopyCount(weight: number) {
  return Math.max(0, Math.round(weight))
}

export function buildBucketBalancedDeck(
  weights: ReadonlyArray<LetterWeight>,
  rngState: number,
  options: BuildBucketBalancedDeckOptions = {},
): { deck: string[]; rngState: number } {
  const rareLetterSet = options.rareLetterSet
  const buckets = Array.from({ length: LETTER_BAG_BUCKET_COUNT }, () => [] as string[])
  let currentRng = rngState
  let bottomRare: string | undefined

  if (rareLetterSet && rareLetterSet.size > 0) {
    const rarePool: string[] = []

    for (const [letter, weight] of weights) {
      if (!rareLetterSet.has(letter)) {
        continue
      }

      const copies = letterWeightToCopyCount(weight)
      for (let index = 0; index < copies; index += 1) {
        rarePool.push(letter)
      }
    }

    if (rarePool.length > 0) {
      currentRng = shuffleInPlace(rarePool, currentRng)
      bottomRare = rarePool[rarePool.length - 1]
      const distributed = distributePoolAcrossBucketsEvenly(
        rarePool,
        LETTER_BAG_BUCKET_COUNT,
        currentRng,
      )
      currentRng = distributed.rngState

      for (let index = 0; index < LETTER_BAG_BUCKET_COUNT; index += 1) {
        buckets[index]!.push(...distributed.buckets[index]!)
      }
    }
  }

  for (const [letter, weight] of weights) {
    if (rareLetterSet?.has(letter)) {
      continue
    }

    const count = letterWeightToCopyCount(weight)
    if (count <= 0) {
      continue
    }

    const baseCopies = Math.floor(count / LETTER_BAG_BUCKET_COUNT)
    const remainder = count % LETTER_BAG_BUCKET_COUNT

    for (let bucketIndex = 0; bucketIndex < LETTER_BAG_BUCKET_COUNT; bucketIndex += 1) {
      for (let copy = 0; copy < baseCopies; copy += 1) {
        buckets[bucketIndex]!.push(letter)
      }
    }

    if (remainder > 0) {
      const picked = pickDistinctBucketIndices(remainder, LETTER_BAG_BUCKET_COUNT, currentRng)
      currentRng = picked.rngState

      for (const bucketIndex of picked.bucketIndices) {
        buckets[bucketIndex]!.push(letter)
      }
    }
  }

  const deck: string[] = []

  for (const bucket of buckets) {
    currentRng = shuffleInPlace(bucket, currentRng)
    deck.push(...bucket)
  }

  if (bottomRare !== undefined) {
    const bottomIndex = deck.indexOf(bottomRare)
    if (bottomIndex > 0) {
      ;[deck[0], deck[bottomIndex]] = [deck[bottomIndex]!, deck[0]!]
    }
  }

  return { deck, rngState: currentRng }
}

export function createBoggleLetterBags(
  classified: ClassifiedLetterWeights,
  rngState: number,
): { bags: BoggleLetterBags; rngState: number } {
  const vowelDeck = buildBucketBalancedDeck(classified.vowels, rngState)
  const consonantDeck = buildBucketBalancedDeck(classified.consonants, vowelDeck.rngState, {
    rareLetterSet: RARE_LETTER_SET,
  })

  return {
    bags: {
      consonants: consonantDeck.deck,
      vowels: vowelDeck.deck,
    },
    rngState: consonantDeck.rngState,
  }
}

export function cloneBoggleLetterBags(bags: BoggleLetterBags): BoggleLetterBags {
  return {
    consonants: [...bags.consonants],
    vowels: [...bags.vowels],
  }
}

function drawFromPile(
  pile: string[],
  weights: ReadonlyArray<LetterWeight>,
  rngState: number,
  options: BuildBucketBalancedDeckOptions,
): { letter: string; pile: string[]; rngState: number } {
  let currentPile = pile
  let currentRng = rngState

  if (currentPile.length === 0) {
    const rebuilt = buildBucketBalancedDeck(weights, currentRng, options)
    currentPile = rebuilt.deck
    currentRng = rebuilt.rngState
  }

  const letter = currentPile[currentPile.length - 1]!
  return {
    letter,
    pile: currentPile.slice(0, -1),
    rngState: currentRng,
  }
}

export function drawLetterFromBag(
  bags: BoggleLetterBags,
  letterClass: keyof ClassifiedLetterWeights,
  classified: ClassifiedLetterWeights,
  rngState: number,
): { bags: BoggleLetterBags; letter: string; rngState: number } {
  const options =
    letterClass === 'consonants' ? { rareLetterSet: RARE_LETTER_SET } : {}
  const drawn = drawFromPile(
    bags[letterClass],
    classified[letterClass],
    rngState,
    options,
  )

  return {
    bags: {
      ...bags,
      [letterClass]: drawn.pile,
    },
    letter: drawn.letter,
    rngState: drawn.rngState,
  }
}

export function drawLettersForClass(
  bags: BoggleLetterBags,
  letterClass: keyof ClassifiedLetterWeights,
  count: number,
  classified: ClassifiedLetterWeights,
  rngState: number,
): { bags: BoggleLetterBags; letters: string[]; rngState: number } {
  const letters: string[] = []
  let currentBags = bags
  let currentRng = rngState

  for (let index = 0; index < count; index += 1) {
    const drawn = drawLetterFromBag(currentBags, letterClass, classified, currentRng)
    currentBags = drawn.bags
    currentRng = drawn.rngState
    letters.push(drawn.letter)
  }

  return {
    bags: currentBags,
    letters,
    rngState: currentRng,
  }
}
